import { AbstractDialog } from "./abstract_dialog.js";
import { DialogId } from "./dialog_id.js";
import { ImgID } from "../../image_loader/img_id.js";
import { RoundedRectangle } from "../common_ui_elements/rounded_rectangle.js";
import { getLinkedSocialProviders, initLinkedProvidersFromServer, LinkedSocialProvider, resetLinkedSocialProviders } from "./account_providers_preview_state.js";
import { DialogManager } from "./dialog_manager.js";
import { ScreenManager, Screens } from "../../screen_manager/screen_manager.js";
import { UIImage } from "../common_ui_elements/ui_image.js";
import { MyTextAlign, MyTextVerticalAlign, UITextElement } from "../common_ui_elements/ui_text_element.js";
import { ColorScheme } from "../common_ui_elements/color_scheme.js";
import { TextStyleScheme } from "../common_ui_elements/text_style_scheme.js";
import { AuthSessionStore } from "../../api/auth_session.js";
import { StatisticsStorage } from "../../storage/statistics_storage.js";
import { ApiError } from "../../api/http_client.js";
import { AuthProvider } from "../../api/dto/auth.dto.js";
import { AvatarUtils } from "../../common/avatar_utils.js";
import { GoogleSignInPlugin } from "../../plugins/play_games_plugin.js";
import { AccountProgressSavedDialog } from "./account_progress_saved_dialog.js";
import { FacebookSignInPlugin } from "../../plugins/facebook_sign_in_plugin.js";
import { connectApple, connectFacebook, connectGoogle } from "../../api/index.js";
import { syncPlayerAfterLogin } from "../../api/player_sync.js";
import { PlayerSyncService } from "../../api/index.js";
import { LinkConflictData } from "../../api/dto/auth.dto.js";
import { AppleSignInPlugin } from "../../plugins/apple_sign_in_plugin.js";
import { AppleWebSignIn } from "../../plugins/apple_web_sign_in.js";


export class SaveDialog extends AbstractDialog
{
  public static readonly dialog_id = DialogId.SAVE_DIALOG;
  private static readonly BUTTON_START_Y = 451;
  private static readonly BUTTON_HEIGHT = 140;
  private static readonly BUTTON_STEP = 168;
  private static readonly BODY_BOTTOM_PADDING = 30;
  private static readonly BUTTONS_BG_Y = 423;

  private bg!: RoundedRectangle;
  private visible_buttons: LinkedSocialProvider[] = [];

  constructor(
    private on_google_linked?: () => void,
    private hidden_buttons: LinkedSocialProvider[] = getLinkedSocialProviders()
  )
  {
    const body_w = LayoutScaler.widthFromDesignAdaptive( 800 );
    const body_h = SaveDialog.getDialogBodyHeight( hidden_buttons );

    const body_x = ( DialogManager.getWidth() - body_w ) / 2;
    const body_y = ( DialogManager.getHeight() - body_h ) / 2;

    super( DialogManager.ctx, body_x, body_y, body_w, body_h );
    this.visible_buttons = SaveDialog.getVisibleButtons( hidden_buttons );

    this.createBackground();
    this.createTitle();
    this.createText();
    this.createButtonsBackground();
    this.createGoogleButton();
    this.createFacebookButton();
    this.createAppleButton();
    this.createCloseIcon();
  }

  private static isIOS(): boolean
  {
    return ( window as any ).Capacitor?.getPlatform?.() === 'ios';
  }

  private static isAndroid(): boolean
  {
    return ( window as any ).Capacitor?.getPlatform?.() === 'android';
  }

  private static getVisibleButtons( hidden_buttons: LinkedSocialProvider[] = [] ): LinkedSocialProvider[]
  {
    return [
      LinkedSocialProvider.Google,
      LinkedSocialProvider.Facebook,
      LinkedSocialProvider.Apple
    ].filter( button => !hidden_buttons.includes( button ) );
  }

  private static getDialogBodyHeight( hidden_buttons: LinkedSocialProvider[] = [] ): number
  {
    const visible_count = SaveDialog.getVisibleButtons( hidden_buttons ).length;
    const buttons_bottom = SaveDialog.BUTTON_START_Y + ( ( visible_count - 1 ) * SaveDialog.BUTTON_STEP ) + SaveDialog.BUTTON_HEIGHT;

    return LayoutScaler.heightFromDesign( buttons_bottom + SaveDialog.BODY_BOTTOM_PADDING );
  }

  private getButtonY( button: LinkedSocialProvider ): number
  {
    const index = this.visible_buttons.indexOf( button );
    return LayoutScaler.yFromDesign( SaveDialog.BUTTON_START_Y + ( index * SaveDialog.BUTTON_STEP ) );
  }

  private refreshDialog( hidden_buttons: LinkedSocialProvider[] = this.hidden_buttons ): void
  {
    DialogManager.routeToDialog( new SaveDialog( this.on_google_linked, hidden_buttons ) );
  }

  private completePreviewLink( player: import('../../api/dto/player.dto.js').PlayerDto ): void
  {
    resetLinkedSocialProviders();
    initLinkedProvidersFromServer( player.linked_providers ?? [] );
    ScreenManager.showScreen( Screens.MENU );
    DialogManager.closeAllDialogs();
    DialogManager.openDialog( new AccountProgressSavedDialog() );
  }

  private createBackground(): void
  {
    this.bg = new RoundedRectangle(
      this.ctx,
      0, 0,
      this.rect.getW(),
      this.rect.getH(),
      {
        tl: LayoutScaler.cornerRadiusFromDesign( 30 ),
        tr: LayoutScaler.cornerRadiusFromDesign( 30 ),
        br: LayoutScaler.cornerRadiusFromDesign( 70 ),
        bl: LayoutScaler.cornerRadiusFromDesign( 70 )
      },
      "#890249"
    );
    this.addChild( this.bg );
  }

  private createCloseIcon(): void
  {
    const icon_size = LayoutScaler.widthFromDesign( 85 );
    const icon_x = this.rect.getW() - icon_size - LayoutScaler.widthFromDesign( 39.8 );
    const icon = new UIImage(
      this.ctx,
      icon_x,
      LayoutScaler.yFromDesign( 39.8 ),
      icon_size,
      icon_size,
      ImgID.UI_SettingsDialogCloseBtn,
      {},
      true
    );
    icon.setOnClick( () => { DialogManager.closeDialog(); } );
    this.addChild( icon );
  }

  private createTitle(): void
  {
    const label_y = LayoutScaler.yFromDesign( 60 );
    const label_h = LayoutScaler.heightFromDesign( 155 );
    const label_w = LayoutScaler.widthFromDesign( 595 );
    const label_x = this.rect.getW2() - label_w / 2;

    const label = new UITextElement(
      this.ctx,
      label_x,
      label_y,
      label_w,
      label_h,
      {
        localization_key: "SAVE_DIALOG.TITLE",
        text_color: ColorScheme.white,
        text_align: MyTextAlign.Center,
        text_style: TextStyleScheme.rubik,
        font_size: LayoutScaler.heightFromDesign( 60 ),
        font_weight: 700,
        auto_wrap: true,
        vertical_align: MyTextVerticalAlign.Center,
      }
    );

    this.addChild( label );
  }

  private createText(): void
  {
    const label_w = LayoutScaler.widthFromDesign( 722 );
    const label_h = LayoutScaler.heightFromDesign( 225 );
    const label_x = this.rect.getW2() - label_w / 2;
    const label_y = LayoutScaler.yFromDesign( 210 );

    const label = new UITextElement(
      this.ctx,
      label_x,
      label_y,
      label_w,
      label_h,
      {
        localization_key: "SAVE_DIALOG.TEXT_TWO",
        text_color: "#FEBEDF",
        text_align: MyTextAlign.Center,
        text_style: TextStyleScheme.rubik,
        font_size: LayoutScaler.heightFromDesign( 40 ),
        font_weight: 700,
        auto_wrap: true,
        vertical_align: MyTextVerticalAlign.Center,
      }
    );

    this.addChild( label );
  }

  private createButtonsBackground(): void
  {
  }

  private createGoogleButton(): void
  {
    if ( !this.visible_buttons.includes( LinkedSocialProvider.Google ) ) return;

    const btn_bg_y = this.getButtonY( LinkedSocialProvider.Google );
    const btn_bg_w = LayoutScaler.widthFromDesign( 757 );
    const btn_bg_x = ( this.rect.getW() - btn_bg_w ) / 2;
    const btn_bg_h = LayoutScaler.heightFromDesign( 140 );

    const google_btn_bg = new RoundedRectangle(
      this.ctx,
      btn_bg_x,
      btn_bg_y,
      btn_bg_w,
      btn_bg_h,
      LayoutScaler.cornerRadiusFromDesign( 70 ),
      "#FFFFFF",
      undefined,
      true
    );

    const google_icon_x = LayoutScaler.xFromDesign( 45 );
    const google_icon_y = btn_bg_y + LayoutScaler.yFromDesign( 19 );
    const google_icon_size = LayoutScaler.widthFromDesign( 100 );

    const google_icon = new UIImage(
      this.ctx,
      google_icon_x - btn_bg_x,
      google_icon_y - btn_bg_y,
      google_icon_size,
      google_icon_size,
      ImgID.GoogleIcon
    );

    const google_text_x = google_icon_x + google_icon_size + LayoutScaler.xFromDesign( 30 );
    const google_text_y = btn_bg_y;
    const google_text_w = btn_bg_x + btn_bg_w - LayoutScaler.xFromDesign( 40 ) - google_text_x;
    const google_text_h = btn_bg_h;

    const google_text = new UITextElement(
      this.ctx,
      google_text_x - btn_bg_x,
      google_text_y - btn_bg_y,
      google_text_w,
      google_text_h,
      {
        localization_key: "SAVE_DIALOG.SIGN_WITH_GOOGLE",
        text_align: MyTextAlign.Center,
        text_color: "#6E0347",
        text_style: TextStyleScheme.rubik,
        font_size: LayoutScaler.textSizeFromDesign( 47 ),
        font_weight: 700,
        vertical_align: MyTextVerticalAlign.Center,
        using_y_offset: false,
        ...TextStyleScheme.buttonShadow(),
      }
    );

    google_btn_bg.addChild( google_icon, true );
    google_btn_bg.addChild( google_text, true );
    this.addChild( google_btn_bg );

    google_btn_bg.setOnClick( async () =>
    {
      console.log( '[SaveDialog] Google button clicked' );
      if ( !GoogleSignInPlugin.isAvailable() )
      {
        console.warn( '[SaveDialog] Google Sign-In not available' );
        return;
      }

      await GoogleSignInPlugin.signOut();

      let idToken: string;
      try
      {
        ( { idToken } = await GoogleSignInPlugin.signIn() );
      }
      catch ( e )
      {
        console.warn( '[SaveDialog] Google Sign-In failed:', e );
        return;
      }

      // const session = AuthSessionStore.get();
      // if ( !session ) return;

      const android_id = await GoogleSignInPlugin.getAndroidId();
      const device_id = android_id ?? localStorage.getItem( 'analytics_userid' ) ?? undefined;

      const extractAndCacheGoogleId = ( token: string ): string =>
      {
        try
        {
          const payload = JSON.parse( atob( token.split( '.' )[ 1 ].replace( /-/g, '+' ).replace( /_/g, '/' ) ) );
          if ( payload.sub )
          {
            GoogleSignInPlugin.setCachedId( payload.sub );
            return payload.sub;
          }
        }
        catch { /* ignore */ }
        return '';
      };

      const googleId = extractAndCacheGoogleId( idToken );

      try
      {
        const player = await connectGoogle( idToken );
        // AuthSessionStore.setFromPlayer( player, AuthProvider.Google, googleId );
        syncPlayerAfterLogin( player );
        StatisticsStorage.onUpdateHook = ( partial ) => { void PlayerSyncService.onStatUpdate( partial ); };
        this.on_google_linked?.();
        this.completePreviewLink( player );
      }
      catch ( e )
      {
        if ( e instanceof ApiError && e.status === 409 )
        {
          const conflict = e.body as LinkConflictData;
          // DialogManager.openDialog( new TwoAccountsDialog( {
          //   current_player:    conflict.current_player,
          //   // current_provider:  session.provider,
          //   existing_player:   conflict.existing_player,
          //   existing_provider: AuthProvider.Google,
          //   on_choose_existing: async () =>
          //   {
          //     const player = await connectGoogle( idToken, 'keep_existing' );
          //     // AuthSessionStore.setFromPlayer( player, AuthProvider.Google, googleId );
          //     loadPlayerFromServer( player );
          //     StatisticsStorage.onUpdateHook = ( partial ) => { void PlayerSyncService.onStatUpdate( partial ); };
          //     this.on_google_linked?.();
          //     this.completePreviewLink( player );
          //   },
          //   on_choose_current: async () =>
          //   {
          //     const player = await connectGoogle( idToken, 'keep_current' );
          //     AuthSessionStore.updateUser( player );
          //     loadPlayerFromServer( player );
          //     StatisticsStorage.onUpdateHook = ( partial ) => { void PlayerSyncService.onStatUpdate( partial ); };
          //     this.completePreviewLink( player );
          //   },
          // } ) );
          return;
        }
        console.warn( '[SaveDialog] Google connect failed:', e );
      }
    } );
  }

  private createFacebookButton(): void
  {
    if ( !this.visible_buttons.includes( LinkedSocialProvider.Facebook ) ) return;

    const btn_bg_y = this.getButtonY( LinkedSocialProvider.Facebook );
    const btn_bg_w = LayoutScaler.widthFromDesign( 757 );
    const btn_bg_x = ( this.rect.getW() - btn_bg_w ) / 2;
    const btn_bg_h = LayoutScaler.heightFromDesign( 140 );

    const facebook_btn_bg = new RoundedRectangle(
      this.ctx,
      btn_bg_x,
      btn_bg_y,
      btn_bg_w,
      btn_bg_h,
      LayoutScaler.cornerRadiusFromDesign( 70 ),
      "#1877F2",
      undefined,
      true
    );

    const facebook_icon_x = LayoutScaler.xFromDesign( 45 );
    const facebook_icon_y = btn_bg_y + LayoutScaler.yFromDesign( 21 );
    const facebook_icon_size = LayoutScaler.widthFromDesign( 100 );

    const facebook_icon = new UIImage(
      this.ctx,
      facebook_icon_x - btn_bg_x,
      facebook_icon_y - btn_bg_y,
      facebook_icon_size,
      facebook_icon_size,
      ImgID.FacebookIcon
    );

    const facebook_text_x = facebook_icon_x + facebook_icon_size + LayoutScaler.xFromDesign( 30 );
    const facebook_text_y = btn_bg_y;
    const facebook_text_w = btn_bg_x + btn_bg_w - LayoutScaler.xFromDesign( 40 ) - facebook_text_x;
    const facebook_text_h = btn_bg_h;

    const facebook_text = new UITextElement(
      this.ctx,
      facebook_text_x - btn_bg_x,
      facebook_text_y - btn_bg_y,
      facebook_text_w,
      facebook_text_h,
      {
        localization_key: "SAVE_DIALOG.SIGN_WITH_FACEBOOK",
        text_align: MyTextAlign.Center,
        text_color: ColorScheme.white,
        text_style: TextStyleScheme.rubik,
        font_size: LayoutScaler.textSizeFromDesign( 47 ),
        font_weight: 700,
        vertical_align: MyTextVerticalAlign.Center,
        using_y_offset: false,
        ...TextStyleScheme.buttonShadow(),
      }
    );

    facebook_btn_bg.addChild( facebook_icon, true );
    facebook_btn_bg.addChild( facebook_text, true );
    this.addChild( facebook_btn_bg );

    facebook_btn_bg.setOnClick( async () =>
    {
      console.log( '[SaveDialog] Facebook button clicked' );
      if ( !FacebookSignInPlugin.isAvailable() )
      {
        console.warn( '[SaveDialog] Facebook Sign-In not available' );
        return;
      }

      await FacebookSignInPlugin.signOut();

      let accessToken: string;
      let facebookId: string;
      try
      {
        ( { accessToken, userId: facebookId } = await FacebookSignInPlugin.signIn() );
      }
      catch ( e )
      {
        console.warn( '[SaveDialog] Facebook Sign-In failed:', e );
        return;
      }

      // const session = AuthSessionStore.get();
      // if ( !session ) return;

      const device_id = localStorage.getItem( 'analytics_userid' ) ?? undefined;

      const applyFacebookPlayer = ( player: import('../../api/dto/player.dto.js').PlayerDto, provider_id: string ) =>
      {
        if ( provider_id ) FacebookSignInPlugin.setCachedId( provider_id );
        // AuthSessionStore.setFromPlayer( player, AuthProvider.Facebook, provider_id );
        syncPlayerAfterLogin( player );
        StatisticsStorage.onUpdateHook = ( partial ) => { void PlayerSyncService.onStatUpdate( partial ); };
      };

      try
      {
        const player = await connectFacebook( accessToken );
        applyFacebookPlayer( player, facebookId );
        this.completePreviewLink( player );
      }
      catch ( e )
      {
        if ( e instanceof ApiError && e.status === 409 )
        {
          const conflict = e.body as LinkConflictData;
          // DialogManager.openDialog( new TwoAccountsDialog( {
          //   current_player:    conflict.current_player,
          //   current_provider:  session.provider,
          //   existing_player:   conflict.existing_player,
          //   existing_provider: AuthProvider.Facebook,
          //   on_choose_existing: async () =>
          //   {
          //     const player = await connectFacebook( accessToken, 'keep_existing' );
          //     if ( facebookId ) FacebookSignInPlugin.setCachedId( facebookId );
          //     AuthSessionStore.setFromPlayer( player, AuthProvider.Facebook, facebookId );
          //     loadPlayerFromServer( player );
          //     StatisticsStorage.onUpdateHook = ( partial ) => { void PlayerSyncService.onStatUpdate( partial ); };
          //     this.completePreviewLink( player );
          //   },
          //   on_choose_current: async () =>
          //   {
          //     const player = await connectFacebook( accessToken, 'keep_current' );
          //     AuthSessionStore.updateUser( player );
          //     loadPlayerFromServer( player );
          //     StatisticsStorage.onUpdateHook = ( partial ) => { void PlayerSyncService.onStatUpdate( partial ); };
          //     this.completePreviewLink( player );
          //   },
          // } ) );
          return;
        }
        console.warn( '[SaveDialog] Facebook connect failed:', e );
      }
    } );
  }

  private createAppleButton(): void
  {
    if ( !this.visible_buttons.includes( LinkedSocialProvider.Apple ) ) return;

    const btn_bg_y = this.getButtonY( LinkedSocialProvider.Apple );
    const btn_bg_w = LayoutScaler.widthFromDesign( 757 );
    const btn_bg_x = ( this.rect.getW() - btn_bg_w ) / 2;
    const btn_bg_h = LayoutScaler.heightFromDesign( 140 );

    const apple_btn_bg = new RoundedRectangle(
      this.ctx,
      btn_bg_x,
      btn_bg_y,
      btn_bg_w,
      btn_bg_h,
      LayoutScaler.cornerRadiusFromDesign( 70 ),
      "#000000",
      undefined,
      true
    );

    const apple_icon_x = LayoutScaler.xFromDesign( 45 );
    const apple_icon_y = btn_bg_y + LayoutScaler.yFromDesign( 21 );
    const apple_icon_size = LayoutScaler.widthFromDesign( 100 );

    const apple_icon = new UIImage(
      this.ctx,
      apple_icon_x - btn_bg_x,
      apple_icon_y - btn_bg_y,
      apple_icon_size,
      apple_icon_size,
      ImgID.AppleIcon
    );

    const apple_text_x = apple_icon_x + apple_icon_size + LayoutScaler.xFromDesign( 30 );
    const apple_text_y = btn_bg_y;
    const apple_text_w = btn_bg_x + btn_bg_w - LayoutScaler.xFromDesign( 40 ) - apple_text_x;
    const apple_text_h = btn_bg_h;

    const apple_text = new UITextElement(
      this.ctx,
      apple_text_x - btn_bg_x,
      apple_text_y - btn_bg_y,
      apple_text_w,
      apple_text_h,
      {
        localization_key: "SAVE_DIALOG.SIGN_WITH_APPLE",
        text_align: MyTextAlign.Center,
        text_color: ColorScheme.white,
        text_style: TextStyleScheme.rubik,
        font_size: LayoutScaler.textSizeFromDesign( 47 ),
        font_weight: 700,
        vertical_align: MyTextVerticalAlign.Center,
        using_y_offset: false,
        ...TextStyleScheme.buttonShadow(),
      }
    );

    apple_btn_bg.addChild( apple_icon, true );
    apple_btn_bg.addChild( apple_text, true );
    this.addChild( apple_btn_bg );

    const applePlugin = ( SaveDialog.isIOS() || SaveDialog.isAndroid() ) ? AppleSignInPlugin : AppleWebSignIn;

    apple_btn_bg.setOnClick( async () =>
    {
      console.log( '[SaveDialog] Apple button clicked' );
      if ( !applePlugin.isAvailable() )
      {
        console.warn( '[SaveDialog] Apple Sign-In not available' );
        return;
      }

      let identityToken: string;
      let appleId: string;
      try
      {
        ( { identityToken, userId: appleId } = await applePlugin.signIn() );
      }
      catch ( e )
      {
        console.warn( '[SaveDialog] Apple Sign-In failed:', e );
        return;
      }

      // const session = AuthSessionStore.get();
      // if ( !session ) return;

      const device_id = localStorage.getItem( 'analytics_userid' ) ?? undefined;

      try
      {
        const player = await connectApple( identityToken );
        if ( appleId ) applePlugin.setCachedId( appleId );
        // AuthSessionStore.setFromPlayer( player, AuthProvider.Apple, appleId );
        syncPlayerAfterLogin( player );
        StatisticsStorage.onUpdateHook = ( partial ) => { void PlayerSyncService.onStatUpdate( partial ); };
        this.completePreviewLink( player );
      }
      catch ( e )
      {
        if ( e instanceof ApiError && e.status === 409 )
        {
          const conflict = e.body as LinkConflictData;
          // DialogManager.openDialog( new TwoAccountsDialog( {
          //   current_player:    conflict.current_player,
          //   current_provider:  session.provider,
          //   existing_player:   conflict.existing_player,
          //   existing_provider: AuthProvider.Apple,
          //   on_choose_existing: async () =>
          //   {
          //     const player = await connectApple( identityToken, 'keep_existing' );
          //     if ( appleId ) applePlugin.setCachedId( appleId );
          //     AuthSessionStore.setFromPlayer( player, AuthProvider.Apple, appleId );
          //     loadPlayerFromServer( player );
          //     StatisticsStorage.onUpdateHook = ( partial ) => { void PlayerSyncService.onStatUpdate( partial ); };
          //     this.completePreviewLink( player );
          //   },
          //   on_choose_current: async () =>
          //   {
          //     const player = await connectApple( identityToken, 'keep_current' );
          //     AuthSessionStore.updateUser( player );
          //     loadPlayerFromServer( player );
          //     StatisticsStorage.onUpdateHook = ( partial ) => { void PlayerSyncService.onStatUpdate( partial ); };
          //     this.completePreviewLink( player );
          //   },
          // } ) );
          return;
        }
        console.warn( '[SaveDialog] Apple connect failed:', e );
      }
    } );
  }

  public render(): void
  {
    if ( !this.shouldBeRendered() ) return;
    this.startRender();
    this.finishRender();
  }

  public name(): string
  {
    return "SaveDialog";
  }

  // public getBackgroundImageId(): ImgID | null
  // {
  //   return ImgID.UI_SaveDialogBg;
  // }
}
